/** Segment Detail sheet — stored inputs vs computed columns (MMBI cost-to-complete). */

export const EXPANSION_SEGMENT_DETAIL_EXPORT_HEADERS = [
  "Seg#",
  "Subsegments",
  "Total Feet",
  "Existing Feet",
  "New Feet",
  "Owner",
  "Overblow Cost",
  "Budget $/Ft",
  "Total Budget",
  "Contractor(s)",
  "Contract ID(s)",
  "Awarded Footage",
  "Awarded Contractor Cost",
  "Actual Contractor $/Ft",
  "Material $/Ft",
  "Material Cost",
  "Awarded Actual Incl. Materials",
  "Awarded Budget Value",
  "Awarded Variance",
  "Remaining Feet to Award",
  "Remaining Budget to Award",
  "Remaining Budget $/Ft",
  "Environmental Allocation",
  "Permitting Allocation",
  "Expected Final Cost",
  "Final Variance vs Budget",
  "% Awarded",
  "Status",
];

/** Persisted per segment (wo_base). Matches Excel columns that are inputs, not formulas. */
export const EXPANSION_SEGMENT_STORED_FIELD_DEFS = [
  { key: "seg_number", label: "Seg#", excelHeader: "Seg#", type: "number", step: "1" },
  {
    key: "subsegment_count",
    label: "Subsegments",
    excelHeader: "Subsegments",
    type: "number",
    readOnly: true,
  },
  { key: "total_feet", label: "Total Feet", excelHeader: "Total Feet", type: "number", step: "0.1", readOnly: true, hint: "Sum of override + construction footage from subsegments" },
  { key: "existing_feet", label: "Existing Feet", excelHeader: "Existing Feet", type: "number", step: "0.1", readOnly: true, hint: "Sum of override footage from subsegments" },
  { key: "new_feet", label: "New Feet", excelHeader: "New Feet", type: "number", step: "0.1", readOnly: true, hint: "Sum of construction footage from subsegments" },
  { key: "owner", label: "Owner", excelHeader: "Owner", type: "text" },
  { key: "overblow_cost", label: "Overblow Cost", excelHeader: "Overblow Cost", type: "number", step: "0.01" },
  { key: "budget_per_ft", label: "Budget $/Ft", excelHeader: "Budget $/Ft", type: "number", step: "0.01" },
  { key: "total_budget", label: "Total Budget", excelHeader: "Total Budget", type: "number", step: "0.01", hint: "Defaults to Total Feet × Budget $/Ft when blank" },
  { key: "contractors", label: "Contractor(s)", excelHeader: "Contractor(s)", type: "text", readOnly: true, hint: "Contractors linked on subsegments via Contracts" },
  { key: "contract_ids", label: "Contract ID(s)", excelHeader: "Contract ID(s)", type: "text", readOnly: true, hint: "Bid package IDs from subsegment contracts" },
  { key: "material_per_ft", label: "Material $/Ft", excelHeader: "Material $/Ft", type: "number", step: "0.01" },
  { key: "environmental_allocation", label: "Environmental Allocation", excelHeader: "Environmental Allocation", type: "number", step: "0.01" },
  { key: "permitting_allocation", label: "Permitting Allocation", excelHeader: "Permitting Allocation", type: "number", step: "0.01" },
];

/** Computed at runtime from stored values + subsegment rollups (not persisted). */
export const EXPANSION_SEGMENT_COMPUTED_HEADERS = [
  "Awarded Footage",
  "Awarded Contractor Cost",
  "Actual Contractor $/Ft",
  "Material Cost",
  "Awarded Actual Incl. Materials",
  "Awarded Budget Value",
  "Awarded Variance",
  "Remaining Feet to Award",
  "Remaining Budget to Award",
  "Remaining Budget $/Ft",
  "Expected Final Cost",
  "Final Variance vs Budget",
  "% Awarded",
];

/** Single landing-page grid — export column order, stored inputs mixed with live rollups. */
export const EXPANSION_SEGMENT_LANDING_FIELD_DEFS = EXPANSION_SEGMENT_DETAIL_EXPORT_HEADERS.filter(
  (header) => header !== "Status"
).map((header) => {
  if (EXPANSION_SEGMENT_COMPUTED_HEADERS.includes(header)) {
    return { header, label: header, computed: true };
  }
  const stored = EXPANSION_SEGMENT_STORED_FIELD_DEFS.find((field) => field.excelHeader === header);
  if (stored) {
    return { header, label: stored.label, stored, hint: stored.hint };
  }
  return { header, label: header, computed: true };
});

export function formatAwardStatusFromPct(pctAwarded) {
  if (pctAwarded == null || !Number.isFinite(Number(pctAwarded))) return null;
  const pct = Number(pctAwarded);
  if (pct >= 1) return "Fully Awarded";
  if (pct <= 0) return "Not Awarded";
  return "Partially Awarded";
}

export const EXPANSION_SEGMENT_STORED_KEYS = EXPANSION_SEGMENT_STORED_FIELD_DEFS.filter(
  (f) => !f.readOnly
).map((f) => f.key);

const EXCEL_STORED_COLUMN_TO_KEY = Object.fromEntries(
  EXPANSION_SEGMENT_STORED_FIELD_DEFS.map((f) => [f.excelHeader, f.key])
);

const TEXT_KEYS = new Set(["owner", "contractors", "contract_ids"]);

export function emptyExpansionSegmentFinancials() {
  return Object.fromEntries(EXPANSION_SEGMENT_STORED_KEYS.map((k) => [k, null]));
}

export function parseFinancialNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function normalizeExpansionSegmentFinancials(raw) {
  const v = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  const out = emptyExpansionSegmentFinancials();
  for (const key of EXPANSION_SEGMENT_STORED_KEYS) {
    if (!(key in v)) continue;
    const val = v[key];
    if (TEXT_KEYS.has(key)) {
      out[key] = val != null && String(val).trim() !== "" ? String(val).trim() : null;
    } else {
      out[key] = parseFinancialNumber(val);
    }
  }
  return out;
}

/** Import only input columns from MMBI Segment Detail — skip formula columns. */
export function mapExcelStoredRowToSegmentFinancials(excelRow) {
  const out = emptyExpansionSegmentFinancials();
  for (const [col, key] of Object.entries(EXCEL_STORED_COLUMN_TO_KEY)) {
    if (!(col in excelRow)) continue;
    const val = excelRow[col];
    if (val === null || val === undefined || val === "") continue;
    if (TEXT_KEYS.has(key)) {
      out[key] = String(val).trim();
    } else {
      out[key] = parseFinancialNumber(val);
    }
  }
  return out;
}

export function normalizeSegNumber(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (Number.isFinite(n)) return n;
  const s = String(value).trim();
  const m = s.match(/\d+/);
  return m ? Number(m[0]) : null;
}

export function segNumbersMatch(a, b) {
  const na = normalizeSegNumber(a);
  const nb = normalizeSegNumber(b);
  if (na == null || nb == null) return false;
  return na === nb;
}
